import React, { useMemo } from "react";

import { Link } from "react-router-dom";

import { PageSection } from "@/components/AppLayout.jsx";

import { ModuleCard } from "@/coquille-bridge";

import { DemoNotice } from "@/components/DemoNotice.jsx";
import { HybridDataStatusBadge } from "@/components/BackendZoneBadge.jsx";

import { AdminGuard } from "@/components/admin/AdminGuard.jsx";

import { useHybridDataStore } from "@/hooks/useHybridDataStore.js";

import { getHanshiDashboardStats, getDojosOverview, getPhase1Preparation } from "@/data/dataStore.js";

import { DataTable } from "@/components/DataTable.jsx";

import { EntityStatusBadge } from "@/components/DocumentVisibilityBadge.jsx";

import { useKarateUi } from "@/hooks/useKarateUi.js";



export function HanshiDashboardPage() {

  const { store, backendStats, backendStatus } = useHybridDataStore();

  const { ui, demoBadge, officialBadge, localizeDemo, prepStatusLabel } = useKarateUi();

  const page = ui.adminPages.dashboard;

  const dash = ui.dashboard;

  const stats = useMemo(() => {
    if (backendStats && backendStatus === "backend") {
      return {
        ...getHanshiDashboardStats(),
        dojosTotal: backendStats.dojosTotal,
        dojosDemo: backendStats.dojosDemo,
        dojosPending: backendStats.dojosPending,
        dojosOfficial: backendStats.dojosOfficial,
        targetDojos: backendStats.targetDojos,
        etudiantsTotal: backendStats.studentsTotal,
        responsablesSeed: backendStats.responsiblesTotal,
      };
    }
    return getHanshiDashboardStats();
  }, [store, backendStats, backendStatus]);

  const prep = useMemo(() => getPhase1Preparation(), [store]);

  const overview = useMemo(() => getDojosOverview(), [store]);

  const statusLabel = prepStatusLabel(stats);



  const pendingDocs = store.documents.filter((d) => d.visibilite === "a_valider");



  return (

    <AdminGuard>

      <PageSection title={page.title} subtitle={page.subtitle} testId="page-admin-hanshi">

        <DemoNotice variant="banner" />
        <HybridDataStatusBadge dataSource={store._dataSource} backendStatus={backendStatus} testId="admin-dashboard-data-badge" />



        <div className="prep-status" data-testid="phase1-prep-status">

          <div className="prep-status__bar">

            <div className="prep-status__fill" style={{ width: `${prep.readinessPercent}%` }} />

          </div>

          <p>

            {ui.prepStatus.label} : <strong>{dash.prepLine(prep.readinessPercent, statusLabel)}</strong>

          </p>

        </div>



        <div className="info-box" data-testid="hanshi-target-note">

          {dash.targetNote(stats.targetDojos, stats.dojosOfficial, stats.dojosDemo, stats.dojosPending)}

        </div>



        <div className="admin-stats">

          <ModuleCard title={dash.statDojos} testId="admin-stat-dojos">

            <p className="stat-number">{stats.dojosTotal}</p>

            <p className="muted">{dash.statDojosDetail(stats.dojosOfficial, stats.dojosDemo)}</p>

            <p className="muted">{dash.statDojosNoSensei(stats.dojosWithoutSensei)}</p>

          </ModuleCard>

          <ModuleCard title={dash.statInstructors} testId="admin-stat-instructeurs">

            <p className="stat-number">{stats.instructeursTotal}</p>

            <p className="muted">{dash.statInstructorsUnassigned(stats.instructeursUnassigned)}</p>

          </ModuleCard>

          <ModuleCard title={dash.statStudents} testId="admin-stat-etudiants">

            <p className="stat-number">{stats.etudiantsTotal}</p>

            <p className="muted">{dash.statStudentsDemo(stats.etudiantsDemo, stats.etudiantsTotal)}</p>

          </ModuleCard>

          <ModuleCard title={dash.statDocsValidate} testId="admin-stat-docs-validate">

            <p className="stat-number">{stats.documentsToValidate}</p>

            <Link to="/admin/documents">{dash.manageDocuments}</Link>

          </ModuleCard>

          <ModuleCard title={dash.statPending} testId="admin-stat-pending">

            <p className="stat-number">{stats.pendingConfirmations}</p>

            <p className="muted">{dash.statPendingDetail}</p>

          </ModuleCard>

          <ModuleCard title={dash.statPhase1} testId="admin-stat-phase1">

            <p className="stat-number">{stats.targetDojos - stats.dojosOfficial}</p>

            <p className="muted">{dash.statPhase1Detail(stats.targetDojos - stats.dojosOfficial)}</p>

          </ModuleCard>

        </div>



        <div className="hanshi-actions" data-testid="hanshi-quick-links">
          <p className="section-h2 hanshi-quick-group__title">{dash.quickGroupAccounts}</p>
          <div className="hanshi-actions__row btn-row">
          <Link to="/admin/comptes" className="btn-primary" data-testid="hanshi-link-comptes">
            {dash.linkAccounts}
          </Link>
          <Link to="/admin/roles" className="btn-secondary" data-testid="hanshi-link-roles">
            {dash.linkRoles}
          </Link>
          <Link to="/admin/demandes" className="btn-secondary" data-testid="hanshi-link-demandes">
            {dash.linkRequests}
          </Link>
          </div>
          <p className="section-h2 hanshi-quick-group__title">{dash.quickGroupData}</p>
          <div className="hanshi-actions__row btn-row">
          <Link to="/admin/import" className="btn-secondary" data-testid="hanshi-link-import">
            {dash.linkImport}
          </Link>
          <Link to="/admin/dojos" className="btn-secondary">
            {dash.linkDojos}
          </Link>
          <Link to="/admin/instructeurs" className="btn-secondary">
            {dash.linkInstructors}
          </Link>
          <Link to="/admin/etudiants" className="btn-secondary">
            {dash.linkStudents}
          </Link>
          <Link to="/admin/documents" className="btn-secondary">
            {dash.linkDocuments}
          </Link>
          </div>
          <p className="section-h2 hanshi-quick-group__title">{dash.quickGroupHelp}</p>
          <div className="hanshi-actions__row btn-row">
          <Link to="/aide" className="btn-secondary">
            {dash.linkHelp}
          </Link>
          <Link to="/formation" className="btn-secondary">
            {dash.linkTraining}
          </Link>
          <Link
            to="/admin/presentation-interactive"
            className="btn-secondary"
            data-testid="hanshi-link-presentation"
          >
            {dash.linkPresentation}
          </Link>
          </div>
        </div>



        <div className="warning-box" data-testid="hanshi-out-of-scope">

          {dash.outOfScope}

        </div>



        <h2 className="section-h2">{dash.overviewTitle}</h2>

        <DataTable

          rows={overview}

          columns={[

            { key: "nom", label: ui.columns.dojo, render: (r) => localizeDemo(r.nom) },

            { key: "ville", label: ui.columns.city, render: (r) => localizeDemo(r.ville) },

            { key: "senseiNom", label: "Sensei", render: (r) => localizeDemo(r.senseiNom ?? "—") },

            { key: "etudiantsCount", label: ui.columns.students },

            {

              key: "statut",

              label: ui.columns.status,

              render: (r) => <EntityStatusBadge statut={r.statut} />,

            },

            {

              key: "demo",

              label: ui.columns.type,

              render: (r) => (r.demo ? demoBadge : officialBadge),

            },

          ]}

          searchFields={["nom", "ville", "senseiNom"]}

          testId="hanshi-dojos-overview"

        />



        {pendingDocs.length > 0 ? (

          <>

            <h2 className="section-h2">{dash.pendingDocsTitle}</h2>

            <ul className="link-list" data-testid="hanshi-pending-docs">

              {pendingDocs.map((d) => (

                <li key={d.id}>

                  {localizeDemo(d.titre)} — <Link to="/admin/documents">{ui.common.validate}</Link>

                </li>

              ))}

            </ul>

            <p className="warning-box">{dash.pendingDocsWarning}</p>

          </>

        ) : null}

      </PageSection>

    </AdminGuard>

  );

}


